import { AsinMetadataEntry } from '@graphql/generated/graphql';
import React, { useEffect, useState } from 'react';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import PlaceholderPng from 'src/images/placeholder.png';
import useGetPrsDataResult from '@store/hooks/useGetPrsDataResult';
import Loader from '@amzn/meridian/loader';
import Column from '@amzn/meridian/column';
import Alert from '@amzn/meridian/alert';
import Modal from '@amzn/meridian/modal';

function getBackgroundColor(arr: Array<string>): string {
  return arr.length === 0 ? '#90db8f' : '#f78181';
}

function getDataCaptureValidationErrors(asin: AsinMetadataEntry): Array<string> {
  const errors: Array<string> = [];
  if (!asin.dimensions) {
    errors.push('Item is missing dimensions');
  }
  if (!asin.weightInGrams) {
    errors.push('Item is missing weight');
  }
  if (!asin.image) {
    errors.push('Item is missing images');
  }
  return errors;
}

function getAsinCreationValidationErrors(asin: AsinMetadataEntry): Array<string> {
  const errors: Array<string> = [];
  if (!asin.title) {
    errors.push(
      'No title found for this product - ensure the ASIN has been published (PAM’d) to the Amazon Go merchant ID (panda02_com).' +
        ' Publishing the ASIN is a prerequisite for imaging.',
    );
  }
  if (!(asin.upc || asin.upce || asin.gtin || asin.ean || asin.plu)) {
    errors.push('This product does not have a linked UPC, UPCE, GTIN, EAN, or PLU');
  }
  return errors;
}

function getOfferCreationValidationErrors(asin: AsinMetadataEntry): Array<string> {
  const errors: Array<string> = [];
  if (!(asin.ourPrice || asin.ourPrice)) {
    errors.push('No price found for this item in this store');
  }
  if (!asin.isSearchable) {
    errors.push('Item is not marked as searchable');
  }
  return errors;
}

function getPrequalificationValidationErrors(asin: AsinMetadataEntry): Array<string> {
  // This minimum weight value comes from https://tiny.amazon.com/jk05v81v/codeamazpackIhmPblob3eaadata
  const MINIMUM_LWP_WEIGHT = 28;

  const errors: Array<string> = [];
  if (
    !asin.techCompatibilities?.includes('LOAD_CELL') &&
    !asin.techCompatibilities?.includes('LWP') &&
    !asin.techCompatibilities?.includes('AUTO_FACING')
  ) {
    errors.push('Item does not have a tech track (ATLAS, AF, or LWP) configured');
    if (asin.weightInGrams && asin.weightInGrams < MINIMUM_LWP_WEIGHT) {
      errors.push(
        `A possible reason this item does not have a tech track configured is because it's under the LWP minimum weight of ${
          MINIMUM_LWP_WEIGHT
        } grams`,
      );
    }
    if (asin.techCompatibilities?.includes('LOAD_CELL')) {
      if (!asin.productStackableForAtlas) {
        errors.push('Item is onboarded for ATLAS, but has no stackability for ATLAS configured');
      }
    }

    if (asin.techCompatibilities?.includes('AUTO_FACING')) {
      if (!asin.maxProductsInLaneForAF) {
        errors.push('Item is onboarded for ATLAS-AF, but has no max products in lane count configured');
      }

      if (!asin.springTensionForAF) {
        errors.push('Item is onboarded for ATLAS-AF, but has no spring tension configured');
      }
    }

    if (asin.techCompatibilities?.includes('LWP')) {
      if (!asin.productStackableForLWP) {
        errors.push('Item is onboarded for LWP, but has no stackability for LWP configured');
      }
    }
  }
  return errors;
}

export interface ProductReadinessTableProps {
  asinList: string[];
  storeId: string;
}

const ProductReadinessTable = (props: ProductReadinessTableProps) => {
  const { asinList, storeId } = props;
  const [dataCaptureErrors, setDataCaptureErrors] = useState(new Map<string, Array<string>>());
  const [asinCreationErrors, setAsinCreationErrors] = useState(new Map<string, Array<string>>());
  const [offerCreationErrors, setOfferCreationErrors] = useState(new Map<string, Array<string>>());
  const [queryError, setQueryError] = useState<string>('');
  const [openError, setOpenError] = useState(true);
  const [prequalificationErrors, setPrequalificationErrors] = useState(new Map<string, Array<string>>());
  const [allErrors, setAllErrors] = useState(new Map<string, Array<string>>());
  const [enableLoader, setEnableLoader] = useState(true);
  const [asinMetadataList, setAsinMetadataList] = useState<AsinMetadataEntry[] | undefined | null>([]);
  const stage = ('gamma' as string).toLowerCase();
  const response = useGetPrsDataResult({
    asinList,
    storeId,
  });
  console.log('STAGE', stage);

  useEffect(() => {
    setEnableLoader(response.asinMetadataList === undefined && !response.error);
    setQueryError(response.error?.message);
    setAsinMetadataList(response.asinMetadataList);
  }, [response]);

  useEffect(() => {
    if (asinMetadataList) {
      const captureErrorsArray = new Map();
      const asinErrorsArray = new Map();
      const offerErrorsArray = new Map();
      const pqErrorsArray = new Map();
      const allErrorsArray = new Map();

      asinMetadataList.forEach((asin) => {
        const dataCaptureErrorsArray = getDataCaptureValidationErrors(asin);
        const asinCreationErrorsArray = getAsinCreationValidationErrors(asin);
        const offerCreationErrorsArray = getOfferCreationValidationErrors(asin);
        const prequalificationErrorsArray = getPrequalificationValidationErrors(asin);

        captureErrorsArray.set(asin.asin, dataCaptureErrorsArray);
        asinErrorsArray.set(asin.asin, asinCreationErrorsArray);
        offerErrorsArray.set(asin.asin, offerCreationErrorsArray);
        pqErrorsArray.set(asin.asin, prequalificationErrorsArray);

        // Combine all errors for each ASIN
        allErrorsArray.set(asin.asin, [
          ...asinCreationErrorsArray,
          ...offerCreationErrorsArray,
          ...prequalificationErrorsArray,
          ...dataCaptureErrorsArray,
        ]);
      });

      setDataCaptureErrors(captureErrorsArray);
      setAsinCreationErrors(asinErrorsArray);
      setOfferCreationErrors(offerErrorsArray);
      setPrequalificationErrors(pqErrorsArray);
      setAllErrors(allErrorsArray);
    }
  }, [asinMetadataList]);

  if (enableLoader) {
    return (
      <Column alignmentHorizontal="center" alignmentVertical="center" spacing="800" spacingInset="500">
        <Loader type="circular" size="large" />{' '}
      </Column>
    );
  }

  if (queryError) {
    return (
      <Modal
        open={openError}
        onClose={() => {
          setOpenError(false);
        }}
      >
        <Alert size="medium" type="error">
          <h3>Error Occurred</h3>
          <p>{queryError}</p>
        </Alert>
      </Modal>
    );
  }

  return (
    <Table showDividers showStripes>
      <TableRow>
        <TableCell>Asin Summary</TableCell>
        <TableCell>Asin Creation</TableCell>
        <TableCell>Offer Creation</TableCell>
        <TableCell>Pre-qualification</TableCell>
        <TableCell>Data Capture</TableCell>
        <TableCell> </TableCell>
      </TableRow>
      {asinMetadataList?.map((asin) => (
        <TableRow>
          <TableCell backgroundColor={getBackgroundColor(allErrors.get(asin.asin) ?? [])}>
            <strong>{asin?.asin}</strong>
            <ul>
              {allErrors.get(asin.asin)?.map((error) => (
                <li>
                  <strong>{error}</strong>
                </li>
              ))}
            </ul>
          </TableCell>
          <TableCell backgroundColor={getBackgroundColor(asinCreationErrors.get(asin.asin) ?? [])}>
            <div>{asin?.title}</div>
            {asin?.upc && <div>upc: {asin.upc}</div>}
            {asin?.upce && <div>upce: {asin.upce}</div>}
            {asin?.gtin && <div>gtin: {asin.gtin}</div>}
            {asin?.ean && <div>ean: {asin.ean}</div>}
            {asin?.plu && <div>plu: {asin.plu}</div>}
            {(stage === 'gamma' || stage === 'prod') && (
              <div>
                <a href={`https://selection.amazon.com/singleItem?asin=${asin?.asin}&merchantName=panda02_com`}>
                  (Selection Central)
                </a>
                <a
                  href={`https://csi.amazon.com/view?view=simple_product_data_view&item_id=${asin?.asin}&marketplace_id=&realm=USAmazon&stage=prod&submit=Show`}
                >
                  (CSI)
                </a>
              </div>
            )}
            {(stage === 'beta' || stage === 'alpha' || stage === 'test') && (
              <div>
                <a
                  href={`https://selection-beta.integ.amazon.com/singleItem?asin=${asin?.asin}&merchantName=panda02_com`}
                >
                  (Selection Central)
                </a>
                <a
                  href={`https://csi.vipinteg.amazon.com/view?view=simple_product_data_view&item_id=${asin?.asin}&marketplace_id=&realm=USAmazon&stage=beta&submit=Show`}
                >
                  (CSI)
                </a>
              </div>
            )}
          </TableCell>
          <TableCell backgroundColor={getBackgroundColor(offerCreationErrors.get(asin.asin) ?? [])}>
            {asin?.ourPrice && (
              <div>
                Our Price:
                {asin?.ourPrice.value} {asin?.ourPrice.unit}
              </div>
            )}
            {asin?.unitPrice && (
              <div>
                Unit Price:
                {asin?.unitPrice?.price?.value} {asin?.unitPrice?.price?.unit} /{asin?.unitPrice.unit}
              </div>
            )}
            <div>{asin?.isSearchable ? 'Searchable' : 'Not Searchable'}</div>
          </TableCell>
          <TableCell backgroundColor={getBackgroundColor(prequalificationErrors.get(asin.asin) ?? [])}>
            {!asin?.techCompatibilities?.includes('LOAD_CELL') &&
              !asin?.techCompatibilities?.includes('LWP') &&
              !asin?.techCompatibilities?.includes('AUTO_FACING') && <div>NO TECH TRACK SELECTED</div>}
            {asin?.techCompatibilities?.includes('LOAD_CELL') && (
              <div>
                ATLAS
                <ul>
                  <li>
                    stackable:
                    {asin?.productStackableForAtlas} {!asin?.productStackableForAtlas && <b>MISSING</b>}
                  </li>
                </ul>
              </div>
            )}
            {asin?.techCompatibilities?.includes('LWP') && (
              <div>
                LWP
                <ul>
                  <li>
                    stackable:
                    {asin?.productStackableForLWP} {!asin?.productStackableForLWP && <b>MISSING</b>}
                  </li>
                </ul>
              </div>
            )}
            {asin?.techCompatibilities?.includes('AUTO_FACING') && (
              <div>
                AUTO_FACING
                <ul>
                  <li>
                    maxProductsInLane:
                    {asin?.maxProductsInLaneForAF} {!asin?.maxProductsInLaneForAF && <b>MISSING</b>}
                  </li>
                  <li>
                    springTension:
                    {asin?.springTensionForAF} {!asin?.springTensionForAF && <b>MISSING</b>}
                  </li>
                </ul>
              </div>
            )}
            {asin?.merchandisingRestrictions?.map((restriction) => (
              <div>{restriction}</div>
            ))}
            {asin?.tidyOrientations?.map((tidyOrientation) => (
              <div>
                {tidyOrientation?.shelfTech} - orientation: {tidyOrientation?.orientation}, rotation:{' '}
                {tidyOrientation?.rotation}
              </div>
            ))}
          </TableCell>
          <TableCell backgroundColor={getBackgroundColor(dataCaptureErrors.get(asin.asin) ?? [])}>
            <ul className="product-images">
              <li>
                <h4>Main</h4>
                {asin?.image ? (
                  <img src={asin?.image} alt="Main Product" />
                ) : (
                  <img src={PlaceholderPng} className="missing-image" alt="Missing main" />
                )}
              </li>
              <li>
                <h4>Menu</h4>
                {asin?.menuImage ? (
                  <img src={asin?.menuImage} alt="Menu" />
                ) : (
                  <img src={PlaceholderPng} className="missing-image" alt="Missing menu" />
                )}
              </li>
              <li>
                <h4>Package Variation</h4>
                {asin?.packageVariantsImage ? (
                  <img src={asin?.packageVariantsImage} alt="Package variation" />
                ) : (
                  <img src={PlaceholderPng} className="missing-image" alt="Missing package variation" />
                )}
              </li>
              <li>
                <h4>Receipt</h4>
                {asin?.receiptImage ? (
                  <img src={asin?.receiptImage} height={200} alt="Receipt" />
                ) : (
                  <img src={PlaceholderPng} className="missing-image" alt="Missing receipt" />
                )}
              </li>
            </ul>
          </TableCell>
          <TableCell backgroundColor={getBackgroundColor(dataCaptureErrors.get(asin.asin) ?? [])}>
            <div>
              {(asin?.dimensions?.widthInMillimeters ||
                asin?.dimensions?.heightInMillimeters ||
                asin?.dimensions?.lengthInMillimeters) && (
                <div>
                  <span>dimensions in mm (W x H x D): </span>
                  {asin?.dimensions?.widthInMillimeters} x {asin?.dimensions?.heightInMillimeters} x{' '}
                  {asin?.dimensions?.lengthInMillimeters}
                </div>
              )}
              {asin?.weightInGrams && (
                <div>
                  <span>weight: </span>
                  {asin?.weightInGrams} grams
                </div>
              )}
            </div>
          </TableCell>
        </TableRow>
      ))}
    </Table>
  );
};

export default ProductReadinessTable;
